'use client';

import { Checkbox, cn } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { useTranslations } from 'next-intl';

type TargetedAdvertisingToggleProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  hasError?: boolean;
};

function TargetedAdvertisingToggle({ checked, onCheckedChange, hasError }: TargetedAdvertisingToggleProps) {
  const t = useTranslations('my_account');
  const { availableChannels } = getSiteConfig().CONTACT_PREFERENCES;

  if (!availableChannels.includes('trustedPartners')) {
    return null;
  }

  const label = t('settings.contact_preferences.channels_labels.targeted_advertising');
  const description = t('settings.contact_preferences.channels_labels.targeted_advertising_description');

  return (
    <div
      className={cn('flex w-full', hasError && 'rounded-md border-1 border-error-foreground p-3')}
      data-testid="targeted-advertising-item"
      data-track-selected={checked ? 'Yes' : 'No'}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <Checkbox
          id="targeted-advertising"
          checked={checked}
          onCheckedChange={value => onCheckedChange(value === true)}
          aria-label={label}
          className={cn(
            'size-7 shrink-0 rounded-md bg-background shadow-none',
            'data-[state=checked]:!border-container-active data-[state=checked]:!bg-container-active',
            'dark:data-[state=checked]:!border-foreground dark:data-[state=checked]:!bg-foreground',
            '[&_[data-slot=checkbox-indicator]]:text-foreground-inverse',
            'dark:[&_[data-slot=checkbox-indicator]_svg]:text-black',
            hasError && 'border-error-foreground'
          )}
          data-testid="targeted-advertising-checkbox"
          data-track-name="Targeted"
          data-track-selected={checked ? 'Yes' : 'No'}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <label htmlFor="targeted-advertising" className="paragraph-explanatory-strong text-foreground">
            {label}
          </label>
          <p className="paragraph-explanatory text-foreground-subtle">{description}</p>
        </div>
      </div>
    </div>
  );
}

export { TargetedAdvertisingToggle };
